import type { AccountLink, AcquisitionAttribution, Activity, Contact, CrmNotification, LifecycleEvent, Opportunity, Organization, RecordNote, ScheduleEvent, Task } from '../types'
import type { CrmRole } from './crmRbac'

export type DataSourceMode = 'loading' | 'live' | 'degraded'
export type DataResource = 'membership' | 'contacts' | 'activities' | 'tasks' | 'organizations' | 'customer-lifecycles' | 'owner-onboarding-readiness' | 'owner-connections' | 'owner-incidents' | 'sales-attributions' | 'commission-accruals' | 'acquisition-attributions' | 'acquisition-touches' | 'acquisition-entities' | 'commercial-source-plans' | 'commercial-source-accruals' | 'agency-partner-profiles' | 'agency-partner-pqv' | 'provider-cost-registry' | 'variable-cost-events' | 'expansion-recommendations' | 'provider-agency-funnels' | 'channel-provenance' | 'channel-review-queue' | 'account-links' | 'lifecycle-events' | 'opportunities' | 'schedules' | 'meeting-participants' | 'notifications' | 'notes' | 'dispositions' | 'call-sessions' | 'availability' | 'availability-blocks' | 'booking-pages' | 'record-assets' | 'prospecting-campaigns' | 'prospect-discoveries' | 'prospect-scans' | 'prospect-issues' | 'prospect-scores' | 'outreach-eligibility' | 'suppression-entries'

export type CrmDataSnapshot = {
  workspaceRole: 'bdr' | 'csr' | 'admin'
  canonicalRole: CrmRole
  contacts: Contact[]
  activities: Activity[]
  tasks: Task[]
  organizations: Organization[]
  accountLinks: AccountLink[]
  lifecycleEvents: LifecycleEvent[]
  opportunities: Opportunity[]
  schedules:ScheduleEvent[]
  notifications:CrmNotification[]
  notes:RecordNote[]
  acquisitionAttributions:AcquisitionAttribution[]
  mode: Exclude<DataSourceMode, 'loading'>
  sources: Record<DataResource, 'live' | 'unavailable'>
  denied?: DataResource[]
  errors: DataResource[]
}

export type RuntimeConfiguration = {
  supabaseUrl: string
  supabaseAnonKey: string
  workspaceId?: string
  apiBase: string
}

function isPublicHttpsOrigin(value: string) {
  try {
    const url = new URL(value)
    return url.protocol === 'https:' && !url.username && !url.password && url.pathname === '/' && !url.search && !url.hash && !['localhost', '127.0.0.1', '::1'].includes(url.hostname)
  } catch {
    return false
  }
}

function isSafeApiBase(value: string) {
  if (/^\/[A-Za-z0-9._~!$&'()*+,;=:@%/-]*$/.test(value) && !value.includes('..') && !value.includes('//')) return true
  return isPublicHttpsOrigin(value)
}

export function validateRuntimeConfiguration(value: RuntimeConfiguration) {
  const issues: string[] = []
  if (value.supabaseUrl && !isPublicHttpsOrigin(value.supabaseUrl)) issues.push('invalid_supabase_url')
  if (value.supabaseUrl && !value.supabaseAnonKey) issues.push('missing_supabase_publishable_key')
  if (value.workspaceId && !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.workspaceId)) issues.push('invalid_workspace_id')
  if (!isSafeApiBase(value.apiBase)) issues.push('invalid_api_base')
  return Object.freeze({ valid: issues.length === 0, issues: Object.freeze(issues) })
}

export function dataSourceMessage(mode: DataSourceMode) {
  if (mode === 'loading') return { title: 'Loading CRM records', detail: 'Verifying the configured data adapters.' }
  if (mode === 'live') return { title: 'Live CRM data', detail: 'Organizations, contacts, opportunities, activity, and tasks loaded through the authenticated workspace boundary.' }
  return { title: 'CRM data partially unavailable', detail: 'One or more canonical adapters failed. Updates are delayed for failed resources; authorized last-good data is retained; no demonstration records are substituted.' }
}
